/**
 * PostHog wrapper for the patient portal.
 *
 * Mirrors the conventions established in the system-web `lib/analytics.js`:
 *   - snake_case event names, noun_verb shape (intake_step_submitted)
 *   - `$pageview` fired manually on route change (capture_pageview disabled
 *     so we control when it fires — useful once we add experiments)
 *   - `identify` keyed on the Supabase user id so funnels join across
 *     anonymous → authed sessions
 *
 * Every public function is a no-op when PostHog isn't configured
 * (missing `VITE_POSTHOG_KEY` in dev). Safe to call unconditionally
 * from feature code.
 */

import posthog from 'posthog-js';
import { maskClinicalReplayRequest } from './analyticsPrivacy';

let initialized = false;
let deferredPatient: { id: string; email?: string | null } | undefined;
let resetBeforeInitialization = false;

function isGuidePath(path: string): boolean {
  try {
    return /^\/guides(?:\/|$)/i.test(decodeURIComponent(path));
  } catch {
    return true;
  }
}

// SDK event and initial-person URLs can contain email links or auth tokens.
// Sanitize the outgoing copy, leaving browser navigation and auth untouched.
function minimizeAnalyticsUrls(properties: Record<string, unknown> | undefined): void {
  if (!properties) return;
  for (const [key, value] of Object.entries(properties)) {
    if (key === '$heatmap_data' && value && typeof value === 'object') {
      const grouped: Record<string, unknown[]> = {};
      for (const [url, points] of Object.entries(value)) {
        if (!Array.isArray(points)) continue;
        const address = { url };
        minimizeAnalyticsUrls(address);
        if (address.url) grouped[address.url] = [...(grouped[address.url] || []), ...points];
      }
      properties[key] = grouped;
    } else if ((key === '$set' || key === '$set_once') && value && typeof value === 'object') {
      minimizeAnalyticsUrls(value as Record<string, unknown>);
    } else if (/url|referrer|path/i.test(key) && typeof value === 'string') {
      if (!/^(?:https?:\/\/|\/)/i.test(value)) continue;
      try {
        const url = new URL(value, window.location.origin);
        // Public guide paths contain bearer capabilities, not route labels.
        const pathname = isGuidePath(url.pathname) ? '/guides/[redacted]' : url.pathname;
        properties[key] = value.startsWith('/')
          ? pathname
          : `${url.origin}${pathname}`;
      } catch {
        delete properties[key];
      }
    }
  }
}

export function initAnalytics(): void {
  if (initialized) return;
  // Do not initialize SDK persistence on a public bearer-link page.
  if (isGuidePath(window.location.pathname)) return;
  // Supabase consumes these asynchronously. Do not persist the initial URL
  // while its fragment still contains a sign-in credential.
  const fragment = new URLSearchParams(window.location.hash.slice(1));
  if (['access_token', 'refresh_token', 'id_token'].some((key) => fragment.has(key))) return;
  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  const host =
    (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ??
    'https://us.i.posthog.com';
  if (!key) {
    // Dev / local without the env var set — silent no-op.
    return;
  }
  try {
    posthog.init(key, {
      api_host: host,
      capture_pageview: false,
      capture_pageleave: true,
      persistence: 'localStorage+cookie',
      // Share the cookie with systemlabs.com so the anonymous_id set during
      // the marketing-side checkout flow continues into the portal. Without
      // this, PostHog scopes its cookie to portal.systemlabs.com and the
      // patient registers as a fresh visitor here, dropping every
      // $experiment_* property + pre-purchase funnel attribution.
      // system-web's lib/analytics.js sets the same flag — both sides have
      // to opt in or the merge doesn't happen.
      cross_subdomain_cookie: true,
      autocapture: false,
      disable_session_recording: import.meta.env.DEV,
      // Keep replay remote configuration; this project does not use flags.
      advanced_disable_feature_flags: true,
      mask_personal_data_properties: true,
      custom_personal_data_properties: [
        'email', 'user', 'handoff', 'token', 'code', 'access_token',
        'refresh_token', 'invite', 'admin_session', 'claim', 'session_id',
      ],
      session_recording: { maskCapturedNetworkRequestFn: maskClinicalReplayRequest },
      before_send: (event) => {
        if (!event) return event;
        minimizeAnalyticsUrls(event.properties);
        minimizeAnalyticsUrls(event.$set);
        minimizeAnalyticsUrls(event.$set_once);
        return event;
      },
    });
    if (resetBeforeInitialization) {
      posthog.reset();
      resetBeforeInitialization = false;
    }
  } catch {
    return;
  }
  initialized = true;
  if (deferredPatient) {
    try {
      posthog.identify(deferredPatient.id, deferredPatient.email ? { email: deferredPatient.email } : {});
    } catch {
      /* swallow */
    }
  }
}

/**
 * Identify the authed patient in PostHog. Keyed on the Supabase user.id —
 * the canonical, immutable identifier per PostHog's docs ("call identify
 * as soon as you're able, typically when a user signs up or logs in"
 * with "your canonical user ID").
 *
 * Marketing-side anonymous sessions on systemlabs.com carry their
 * anonymous_id into portal.systemlabs.com via the default cross-subdomain
 * cookie (cross_subdomain_cookie defaults to true). When identifyPatient
 * runs here on auth, PostHog merges that anonymous_id into the user.id
 * person — which is the one identify pattern PostHog explicitly allows
 * (anonymous → identified). No identify on the marketing side is needed.
 */
export function identifyPatient(opts: { id: string; email?: string | null }): void {
  deferredPatient = { ...opts };
  if (!initialized) {
    initAnalytics();
    return;
  }
  try {
    posthog.identify(opts.id, opts.email ? { email: opts.email } : {});
  } catch {
    /* swallow */
  }
}

/**
 * Stamp experiment cohorts onto the PostHog *person* (keyed `$experiment_<key>`
 * to match what system-web sets on the marketing side) so portal events can be
 * broken down by variant. This is the portal's own attribution — it does NOT
 * rely on the marketing→portal anonymous person stitching (which is unreliable
 * across the redirect). Person-property breakdowns are retroactive (PostHog
 * uses the person's latest value), so calling this whenever the authed
 * assignments resolve attributes ALL of the patient's events — no
 * pageview/identify race. Call after `identifyPatient` (assignments come from
 * an authed endpoint, so the patient is already identified).
 */
export function setExperimentPersonProperties(
  assignments: Record<string, string> | null | undefined,
): void {
  initAnalytics();
  if (!initialized || !assignments) return;
  const props: Record<string, string> = {};
  for (const [key, variant] of Object.entries(assignments)) {
    if (key && variant) props[`$experiment_${key}`] = variant;
  }
  if (Object.keys(props).length === 0) return;
  try {
    posthog.setPersonProperties(props);
  } catch {
    /* swallow */
  }
}

export function resetIdentity(): void {
  deferredPatient = undefined;
  if (!initialized) {
    resetBeforeInitialization = true;
    return;
  }
  try {
    posthog.reset();
  } catch {
    /* swallow */
  }
}

export function track(event: string, properties?: Record<string, unknown>): void {
  initAnalytics();
  if (!initialized) return;
  try {
    posthog.capture(event, properties);
  } catch {
    /* swallow */
  }
}

export function trackPageview(path: string, props?: Record<string, unknown>): void {
  initAnalytics();
  if (!initialized) return;
  try {
    posthog.capture('$pageview', { path, ...(props ?? {}) });
  } catch {
    /* swallow */
  }
}
